import { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../lib/api';
import { Card } from '../ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import { Ban, Filter, RotateCcw, Users } from 'lucide-react';

export const CRED_STATUS = [
  { v: 'not_enrolled', l: 'Not Enrolled' },
  { v: 'pending', l: 'Pending' },
  { v: 'active', l: 'Active' },
  { v: 'inactive', l: 'Inactive' },
  { v: 'group_covered', l: 'Group contract' },
  { v: 'not_started', l: 'Not Started' },
  { v: 'rejected', l: 'Rejected' },
  { v: 'oon', l: 'Out of Network' },
];
const TONE = {
  active: 'bg-green-100 text-green-800 border-green-200',
  pending: 'bg-amber-100 text-amber-800 border-amber-200',
  inactive: 'bg-slate-100 text-slate-700 border-slate-200',
  not_enrolled: 'bg-white text-slate-500 border-dashed border-slate-300',
  group_covered: 'bg-[#EEF4F2] text-[#3F7D6E] border-[#C9DAD2]',
};
const STATUS_FILTERS = [{ v: 'all', l: 'All statuses' }, ...CRED_STATUS];
const fullName = (p) => `${p.first_name} ${p.last_name}`.trim();

const PickList = ({ label, items, selected, onChange, testid }) => (
  <Popover>
    <PopoverTrigger asChild>
      <button className="h-8 px-3 rounded-full border border-[#E5E0EA] bg-white text-[12px] text-[#5E5068] flex items-center gap-1.5" data-testid={testid}>
        <Filter size={12} /> {label}{selected.length ? ` · ${selected.length}` : ''}
      </button>
    </PopoverTrigger>
    <PopoverContent className="w-72 max-h-80 overflow-y-auto p-2" align="start">
      <button className="text-[11px] text-[#685B72] underline mb-1" onClick={() => onChange([])} data-testid={`${testid}-clear`}>Show all</button>
      {items.map((it) => (
        <label key={it.id} className="flex items-center gap-2 py-1 text-[12px] cursor-pointer">
          <Checkbox checked={selected.includes(it.id)} data-testid={`${testid}-opt-${it.id}`}
            onCheckedChange={() => onChange(selected.includes(it.id) ? selected.filter((x) => x !== it.id) : [...selected, it.id])} />
          {it.label}
        </label>
      ))}
    </PopoverContent>
  </Popover>
);

const PayerHeader = ({ y, count, onToggleGroup, onToggleNR }) => (
  <th className={`p-2 border-b text-left align-bottom min-w-[150px] ${y.credential_not_required ? 'bg-slate-50' : y.group_covered ? 'bg-[#F4F8F6]' : ''}`} data-testid={`cred-col-${y.payer_id}`}>
    <div className="font-semibold text-slate-700 whitespace-nowrap" title={y.children.length ? `Includes: ${y.children.join(', ')}` : ''}>
      {y.name}{y.children.length > 0 && <span className="ml-1 text-[10px] font-normal text-slate-400">+{y.children.length} plan{y.children.length === 1 ? '' : 's'}</span>}
    </div>
    {!y.credential_not_required && (
      <div className={`text-[10px] font-semibold mt-0.5 ${count.active === count.total ? 'text-[#3F7D6E]' : 'text-[#5E5068]'}`} data-testid={`cred-col-count-${y.payer_id}`}>
        {count.active}/{count.total} active{count.pending ? ` · ${count.pending} pending` : ''}
      </div>
    )}
    <label className="mt-1 flex items-center gap-1 text-[10px] font-normal text-slate-500 cursor-pointer whitespace-nowrap">
      <Checkbox checked={y.group_covered} onCheckedChange={() => onToggleGroup(y)} className="h-3 w-3" data-testid={`cred-group-${y.payer_id}`} />
      Group contract
    </label>
    <label className="flex items-center gap-1 text-[10px] font-normal text-slate-500 cursor-pointer whitespace-nowrap">
      <Checkbox checked={y.credential_not_required} onCheckedChange={() => onToggleNR(y)} className="h-3 w-3" data-testid={`cred-nr-${y.payer_id}`} />
      No credentialing needed
    </label>
  </th>
);

const MAJOR = [/\bBCBS\b|blue cross/i, /\bUHC\b|united\s*health/i];
const majorRank = (name) => { const i = MAJOR.findIndex((re) => re.test(name)); return i === -1 ? 99 : i; };

// Order: major payers → others by most pending → name; group contracts, then not-required, at the far right.
const orderPayers = (payers, counts) => [...payers].sort((a, b) => {
  const tier = (y) => (y.credential_not_required ? 2 : y.group_covered ? 1 : 0);
  return tier(a) - tier(b) || majorRank(a.name) - majorRank(b.name)
    || (counts[b.payer_id]?.pending || 0) - (counts[a.payer_id]?.pending || 0) || a.name.localeCompare(b.name);
});

const Summary = ({ providers, payers, counts, statusOf }) => {
  const active = providers.filter((p) => !p.credentialing_excluded);
  const work = payers.filter((y) => !y.credential_not_required && !y.group_covered);
  const tot = (k) => work.reduce((n, y) => n + (counts[y.payer_id]?.[k] || 0), 0);
  const cells = work.length * active.length;
  const pct = cells ? Math.round((tot('active') / cells) * 100) : null;
  const none = active.filter((p) => work.length && !work.some((y) => ['active', 'group_covered'].includes(statusOf(p.provider_id, y.payer_id))));
  const empty = work.filter((y) => !counts[y.payer_id]?.active);
  const msgs = [
    tot('pending') && `${tot('pending')} credentialing application${tot('pending') === 1 ? ' is' : 's are'} pending — follow up with the payers.`,
    none.length && `${none.length} provider${none.length === 1 ? ' has' : 's have'} no active payer yet: ${none.slice(0, 4).map(fullName).join(', ')}${none.length > 4 ? '…' : ''}.`,
    empty.length && `No one is active with ${empty.slice(0, 4).map((y) => y.name).join(', ')}${empty.length > 4 ? ` and ${empty.length - 4} more` : ''}.`,
    tot('inactive') && `${tot('inactive')} enrollment${tot('inactive') === 1 ? ' is' : 's are'} inactive — check if they need re-credentialing.`,
  ].filter(Boolean);
  const Stat = ({ l, v, tone, tid }) => (
    <div className="rounded-xl border border-[#E5E0EA] bg-white px-3 py-2" data-testid={tid}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{l}</div>
      <div className={`text-xl font-serif ${tone || 'text-[#2D2833]'}`}>{v}</div>
    </div>
  );
  return (
    <div className="mb-4 space-y-3" data-testid="cred-summary">
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
        <Stat l="Providers credentialing" v={active.length} tid="cred-sum-providers" />
        <Stat l="Payers to credential" v={work.length} tid="cred-sum-payers" />
        <Stat l="Active" v={tot('active')} tone="text-[#3F7D6E]" tid="cred-sum-active" />
        <Stat l="Pending" v={tot('pending')} tone="text-[#8A6A1E]" tid="cred-sum-pending" />
        <Stat l="Not enrolled" v={tot('not_enrolled')} tid="cred-sum-not-enrolled" />
        <Stat l="Inactive" v={tot('inactive')} tid="cred-sum-inactive" />
        <Stat l="Complete" v={pct === null ? '—' : `${pct}%`} tone={pct === 100 ? 'text-[#3F7D6E]' : 'text-[#5E5068]'} tid="cred-sum-pct" />
        <Stat l="Group / not needed" v={`${payers.filter((y) => y.group_covered).length} / ${payers.filter((y) => y.credential_not_required).length}`} tid="cred-sum-group" />
      </div>
      {msgs.length > 0 && (
        <div className="rounded-xl border border-[#F0DDB0] bg-[#FFF9EC] px-4 py-2 text-[12px] text-[#6B5418] space-y-0.5" data-testid="cred-attention">
          <div className="font-semibold uppercase tracking-wider text-[10px]">Needs attention</div>
          {msgs.map((m) => <div key={m}>• {m}</div>)}
        </div>
      )}
    </div>
  );
};

const ProviderCell = ({ p, onToggleExclude }) => (
  <td className="sticky left-0 bg-inherit p-2 border-b border-r font-medium whitespace-nowrap" data-testid={`cred-provider-${p.provider_id}`}>
    <div className="flex items-center justify-between gap-2">
      <span>{fullName(p)}</span>
      <button onClick={() => onToggleExclude(p)} data-testid={`cred-exclude-${p.provider_id}`}
        title={p.credentialing_excluded ? 'Start credentialing this provider again' : "Mark as Don't credential (moves to bottom)"}
        className="text-slate-400 hover:text-[#685B72]">
        {p.credentialing_excluded ? <RotateCcw size={13} /> : <Ban size={13} />}
      </button>
    </div>
    {p.credentialing_excluded && <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Don't credential</div>}
  </td>
);

export function CredentialingMatrix({ query, matches }) {
  const [data, setData] = useState({ providers: [], payers: [], cells: {} });
  const [loading, setLoading] = useState(true);
  const [statusF, setStatusF] = useState('all');
  const [payerF, setPayerF] = useState([]);
  const [provF, setProvF] = useState([]);

  const load = useCallback(async () => {
    try { const { data } = await api.get('/vault/credentialing'); setData(data); }
    catch { toast.error('Could not load credentialing matrix'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const statusOf = useCallback((pid, yid) => data.cells?.[pid]?.[yid]?.status || 'not_enrolled', [data.cells]);
  const counts = useMemo(() => {
    const act = (data.providers || []).filter((p) => !p.credentialing_excluded);
    const out = {};
    (data.payers || []).forEach((y) => {
      const c = { total: act.length, active: 0, pending: 0, not_enrolled: 0, inactive: 0 };
      act.forEach((p) => {
        const s = statusOf(p.provider_id, y.payer_id);
        if (s === 'active' || s === 'group_covered') c.active += 1;
        else if (c[s] !== undefined) c[s] += 1;
      });
      out[y.payer_id] = c;
    });
    return out;
  }, [data.providers, data.payers, statusOf]);
  // Search narrows only the axis it matches, so the grid never empties out.
  const payerHit = (y) => matches(query, y.name, ...(y.children || []));
  const provHit = (p) => matches(query, p.first_name, p.last_name);
  const anyPayerHit = !query || (data.payers || []).some(payerHit);
  const anyProvHit = !query || (data.providers || []).some(provHit);
  const payers = useMemo(() => {
    const vis = (data.payers || []).filter((y) => (!payerF.length || payerF.includes(y.payer_id))
      && (!anyPayerHit || !query || payerHit(y)));
    return orderPayers(vis, counts);
  }, [data.payers, payerF, query, anyPayerHit, counts]); // eslint-disable-line react-hooks/exhaustive-deps
  const providers = useMemo(() => (data.providers || []).filter((p) => (!provF.length || provF.includes(p.provider_id))
    && (!anyProvHit || !query || provHit(p))
    && (statusF === 'all' || (!p.credentialing_excluded && payers.some((y) => statusOf(p.provider_id, y.payer_id) === statusF)))),
  [data.providers, provF, query, statusF, payers, statusOf, anyProvHit]); // eslint-disable-line react-hooks/exhaustive-deps

  const setCell = async (provider_id, payer_id, status) => {
    try {
      const { data: updated } = await api.put('/vault/credentialing', { provider_id, payer_id, status });
      setData((d) => ({ ...d, cells: { ...d.cells, [provider_id]: { ...(d.cells[provider_id] || {}), [payer_id]: updated } } }));
    } catch { toast.error('Could not update status'); }
  };
  const toggleGroup = async (y) => {
    try {
      await api.put('/vault/credentialing/payer-group', { payer_id: y.payer_id, group_covered: !y.group_covered });
      toast.success(y.group_covered ? `${y.name}: group contract removed` : `${y.name}: all providers covered by group contract`);
      load();
    } catch { toast.error('Could not update payer'); }
  };
  const toggleNR = async (y) => {
    try {
      await api.put('/vault/credentialing/payer-not-required', { payer_id: y.payer_id, not_required: !y.credential_not_required });
      toast.success(y.credential_not_required ? `${y.name}: credentialing tracked again` : `${y.name}: no credentialing needed (moved right)`);
      load();
    } catch { toast.error('Could not update payer'); }
  };
  const toggleExclude = async (p) => {
    try {
      await api.put('/vault/credentialing/provider-exclude', { provider_id: p.provider_id, excluded: !p.credentialing_excluded });
      toast.success(p.credentialing_excluded ? `${fullName(p)} is back in credentialing` : `${fullName(p)} marked Don't credential`);
      load();
    } catch { toast.error('Could not update provider'); }
  };

  if (loading) return <Card className="p-6">Loading…</Card>;
  return (
    <Card className="p-4" data-testid="vault-credentialing">
      <Summary providers={data.providers || []} payers={data.payers || []} counts={counts} statusOf={statusOf} />
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <select value={statusF} onChange={(e) => setStatusF(e.target.value)} data-testid="cred-filter-status"
          className="h-8 px-3 rounded-full border border-[#E5E0EA] bg-white text-[12px] text-[#5E5068]">
          {STATUS_FILTERS.map((s) => <option key={s.v} value={s.v}>{s.v === 'all' ? s.l : `Has ${s.l}`}</option>)}
        </select>
        <PickList label="Payers" testid="cred-filter-payers" selected={payerF} onChange={setPayerF}
          items={(data.payers || []).map((y) => ({ id: y.payer_id, label: y.name }))} />
        <PickList label="Providers" testid="cred-filter-providers" selected={provF} onChange={setProvF}
          items={(data.providers || []).map((p) => ({ id: p.provider_id, label: fullName(p) }))} />
        <span className="ml-auto text-[11px] text-slate-500 flex items-center gap-1" data-testid="cred-count">
          <Users size={12} /> {providers.length} providers · {payers.length} parent payers
        </span>
      </div>
      <div className="overflow-auto">
        <table className="min-w-full text-xs border-collapse">
          <thead><tr>
            <th className="sticky left-0 z-10 bg-white text-left p-2 border-b border-r min-w-[200px]">Provider ↓ / Payer →</th>
            {payers.map((y) => <PayerHeader key={y.payer_id} y={y} count={counts[y.payer_id] || {}} onToggleGroup={toggleGroup} onToggleNR={toggleNR} />)}
          </tr></thead>
          <tbody>
            {providers.map((p) => (
              <tr key={p.provider_id} className={p.credentialing_excluded ? 'bg-slate-50 opacity-60' : 'bg-white'} data-testid={`cred-row-${p.provider_id}`}>
                <ProviderCell p={p} onToggleExclude={toggleExclude} />
                {payers.map((y) => {
                  const cell = data.cells?.[p.provider_id]?.[y.payer_id];
                  const status = cell?.status || 'not_enrolled';
                  const dim = statusF !== 'all' && status !== statusF;
                  if (y.credential_not_required) {
                    return <td key={y.payer_id} className="p-1 border-b bg-slate-50 text-[10px] text-slate-400 text-center" data-testid={`cred-cell-${p.provider_id}-${y.payer_id}`}>Not needed</td>;
                  }
                  return (
                    <td key={y.payer_id} className={`p-1 border-b ${dim ? 'opacity-30' : ''}`}>
                      <select value={status} disabled={p.credentialing_excluded}
                        onChange={(e) => setCell(p.provider_id, y.payer_id, e.target.value)}
                        title={cell?.merged_from ? `Carried over from ${cell.merged_from}` : cell?.auto ? 'Covered by group contract' : ''}
                        data-testid={`cred-cell-${p.provider_id}-${y.payer_id}`}
                        className={`w-full text-xs px-2 py-1 rounded border ${TONE[status] || TONE.not_enrolled}`}>
                        {CRED_STATUS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
                      </select>
                    </td>
                  );
                })}
              </tr>
            ))}
            {providers.length === 0 && <tr><td colSpan={payers.length + 1} className="p-6 text-center text-slate-500" data-testid="cred-empty">{(data.providers || []).length ? 'No providers match these filters.' : 'No providers to credential yet — add them in Provider Roster (Ludo: BCBAs / BCaBAs only).'}</td></tr>}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export default CredentialingMatrix;
